import { Controller } from "@hotwired/stimulus"

const RENDER_CAP = 500

// Owns the staged code pool. Nothing persists until the privilege form is
// submitted: everything here writes into the hidden privilege[codes] field.
export default class extends Controller {
  static targets = [
    "dialog", "hiddenField", "textarea", "file", "replace",
    "list", "count", "filter", "summary", "capNote"
  ]

  connect() {
    this.codes = this.parse(this.hiddenFieldTarget.value)
    this.updateSummary()
  }

  open() {
    this.snapshot = [...this.codes]
    this.filterTarget.value = ""
    this.textareaTarget.value = ""
    this.render()
    this.dialogTarget.classList.remove("hidden")
  }

  close() { this.dialogTarget.classList.add("hidden") }

  closeOnBackdrop(event) {
    if (event.target === event.currentTarget) this.cancel()
  }

  // Done keeps the staged edits; Cancel reverts to the pool as it was on open.
  done() { this.close() }

  cancel() {
    this.codes = [...this.snapshot]
    this.commit()
    this.close()
  }

  add() {
    const pasted = this.parseText(this.textareaTarget.value)
    this.codes = this.replaceTarget.checked ? pasted : this.dedupe([...this.codes, ...pasted])
    this.textareaTarget.value = ""
    this.replaceTarget.checked = false
    this.commit()
    this.render()
  }

  // Read client-side; nothing is uploaded.
  readFile() {
    const file = this.fileTarget.files[0]
    if (!file) return

    const reader = new FileReader()
    reader.onload = () => {
      const fromFile = this.parseText(reader.result)
      this.codes = this.replaceTarget.checked ? fromFile : this.dedupe([...this.codes, ...fromFile])
      this.fileTarget.value = ""
      this.replaceTarget.checked = false
      this.commit()
      this.render()
    }
    reader.readAsText(file)
  }

  remove(event) {
    const value = event.currentTarget.dataset.code
    this.codes = this.codes.filter((c) => c !== value)
    this.commit()
    this.render()
  }

  filter() { this.render() }

  // One parser for both the textarea and the file, so paste and upload cannot
  // diverge. A multi-column CSV takes the first column.
  parseText(text) {
    const entries = String(text || "")
      .split("\n")
      .map((line) => line.split(",")[0])
      .map((entry) => entry.trim())
      .filter((entry) => entry.length > 0)

    return this.dedupe(entries)
  }

  // Case-sensitive: ABC and abc are different codes.
  dedupe(list) { return [...new Set(list)] }

  parse(json) {
    try {
      const parsed = JSON.parse(json)
      return Array.isArray(parsed) ? parsed.map(String) : []
    } catch {
      return []
    }
  }

  commit() {
    this.hiddenFieldTarget.value = JSON.stringify(this.codes)
    this.updateSummary()
  }

  updateSummary() {
    const label = `${this.codes.length.toLocaleString()} codes`
    this.summaryTargets.forEach((el) => { el.textContent = label })
  }

  render() {
    const needle = this.filterTarget.value.trim().toLowerCase()
    const matching = needle
      ? this.codes.filter((c) => c.toLowerCase().includes(needle))
      : this.codes
    const shown = matching.slice(0, RENDER_CAP)

    this.countTarget.textContent = `${this.codes.length.toLocaleString()} codes`

    this.listTarget.replaceChildren(...shown.map((code) => this.row(code)))

    this.capNoteTarget.hidden = matching.length <= RENDER_CAP
    this.capNoteTarget.textContent =
      `showing ${RENDER_CAP.toLocaleString()} of ${matching.length.toLocaleString()}`
  }

  // Built with DOM nodes rather than innerHTML so a code containing markup
  // cannot inject anything.
  row(code) {
    const row = document.createElement("div")
    row.className = "flex items-center justify-between py-1.5 px-2 border-b border-gray-100 text-sm"

    const label = document.createElement("span")
    label.className = "font-mono text-gray-700"
    label.textContent = code

    const button = document.createElement("button")
    button.type = "button"
    button.className = "text-gray-400 hover:text-red-600 px-2"
    button.textContent = "×"
    button.dataset.code = code
    button.dataset.action = "click->codes-modal#remove"

    row.append(label, button)
    return row
  }
}
